import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PartQuestionList from "./PartQuestionList";
import { useSelector } from "react-redux";
import { QuestionForm } from "@/components/common";
import {
  useAddQuestionToExamMutation,
  useGetExamByIdQuery,
  useDeleteQuestionFromExamMutation,
  useUpdateQuestionInExamMutation,
  useParsePdfMutation,
  useBulkAddExamQuestionsMutation,
} from "@/store/api/adminApi";
import { SECTION } from "@/constants/examConstants";
import { toast } from "react-toastify";
import { delay } from "@/utils/common";
import { uploadMediaFiles } from "@/utils/cloudinaryUpload";
import PdfUploadDialog from "@/components/admin/PdfUploadDialog";
import PdfReviewDialog from "@/components/admin/PdfReviewDialog";

const ExamQuestionManagement = ({ examId, examTitle, part }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [formData, setFormData] = useState({});
  const [questions, setQuestions] = useState([]);
  const formikRef = useRef(null);
  const questionFormRef = useRef(null);

  // PDF upload (RC parts 5/6/7 only)
  const [pdfUploadOpen, setPdfUploadOpen] = useState(false);
  const [pdfParseResult, setPdfParseResult] = useState(null);
  const [pdfResultsOpen, setPdfResultsOpen] = useState(false);
  const parsePdfMutation = useParsePdfMutation();
  const bulkAddExamQuestionsTuple = useBulkAddExamQuestionsMutation();
  const isRcPart = [5, 6, 7].includes(part?.partNumber);

  const { data: examData, isLoading: examLoading } =
    useGetExamByIdQuery(examId);
  const [addQuestion] = useAddQuestionToExamMutation();
  const [updateQuestion] = useUpdateQuestionInExamMutation();
  const [deleteQuestion] = useDeleteQuestionFromExamMutation();
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { uploadProgress } = useSelector((state) => state.upload);
  const [previousQuestion, setPreviousQuestion] = useState(null);

  useEffect(() => {
    if (formData?.question_number) {
      const questionIndex = questions.findIndex(
        (q) => q.number === formData.question_number - 1
      );
      if (questionIndex >= 0) {
        setPreviousQuestion(questions[questionIndex]);
      }
    }
  }, [formData?.question_number]);

  useEffect(() => {
    if (questions?.length) {
      setFormData({
        ...formData,
        question_number:
          formData.question_number ||
          (questions?.[questions?.length - 1]?.number || 0) + 1,
      });

      // setQuestions(questionList);
    }
  }, [part, dialogOpen, questions]);

  const handleOpenDialog = (question = null, part = null) => {
    if (question) {
      setEditingQuestion(question);
      const previousQuestionIndex = questions.findIndex(
        (q) => q._id === question._id
      );
      setPreviousQuestion(questions[previousQuestionIndex - 1]);
      const options = question.choices?.map((choice) => choice.text) || ["", "", "", ""];
      const correctChoice = question.choices.find((choice) => choice.isCorrect);
      const correctIndex = correctChoice ? question.choices.indexOf(correctChoice) : -1;

      setFormData({
        ...question,
        options: options,
        audioPreview: question.audioUrl,
        imagePreview: question.imageUrl,
        extraImagePreview: question.extraImageUrl,
        question_number: question.number,
        content: question.content && question.content !== "undefined" && question.content !== "<br>" ? question.content : "",
        correctAnswer: correctIndex >= 0 ? String.fromCharCode(65 + correctIndex).toLowerCase() : "",
        explanation: question.explanation || "",
        updated_at: new Date().toISOString(),
      });
    } else {
      setPreviousQuestion(questions[questions.length - 1]);

      setEditingQuestion(null);
      setFormData({
        type: "multiple-choice",
        partId: part?._id || "",
        partNumber: part?.partNumber || 1,
        question_number: questions.length > 0 ? questions[questions.length - 1].number + 1 : 1,
        question: "",
        content: "",
        options: ["", "", "", ""],
        correctAnswer: "",
        explanation: "",
        image: null,
        extraImage: null,
        audio: null,
        order: 1,
        exam_id: examId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingQuestion(null);
    // Reset rich text editors
    if (questionFormRef.current) {
      questionFormRef.current.resetRichText();
    }
  };

  const handleFormDataChange = (updatedFormData) => {
    setFormData(updatedFormData);
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      setError(null);
      
      // Process images in rich text editors and get final data
      let currentFormData = formData;
      if (questionFormRef.current) {
        currentFormData = await questionFormRef.current.processImagesAndGetData();
      }
      
      // Upload media files directly to Cloudinary
      const mediaFiles = {
        image: currentFormData.image,
        audio: currentFormData.audio,
        extraImage: currentFormData.extraImage,
      };
      
      const uploadedUrls = await uploadMediaFiles(mediaFiles, `toeic/exams/${examId}`);
      
      // Always map all options, even if empty, with isCorrect flag and value
      const choices = currentFormData.options.map((option, index) => {
        const value = String.fromCharCode(65 + index);
        const optionText = typeof option === 'string' ? option : option.text;
        return {
          text: optionText,
          value: value,
          isCorrect: value.toLowerCase() === currentFormData.correctAnswer?.toLowerCase(),
        };
      });

      // Validate that at least one option is marked as correct
      const hasCorrectAnswer = choices.some((choice) => choice.isCorrect);
      if (!hasCorrectAnswer) {
        throw new Error("Please select the correct answer");
      }

      const questionData = new FormData();

      // Append all form fields to FormData
      questionData.append("type", currentFormData.type);
      questionData.append("number", currentFormData.question_number);
      questionData.append("question", currentFormData.question);
      questionData.append("choices", JSON.stringify(choices));
      questionData.append("explanation", currentFormData.explanation);
      questionData.append("tags", JSON.stringify(currentFormData.tags || []));
      questionData.append("partNumber", JSON.stringify(currentFormData.partNumber));
      questionData.append("order", currentFormData.order);
      
      const sanitizedContent = currentFormData.content
        ? currentFormData.content.replace(/<[^>]*>/g, "").trim()
        : "";

      questionData.append(
        "content",
        sanitizedContent.length > 0 ? currentFormData.content : ""
      );

      // Append uploaded URLs or existing URLs
      ["image", "audio", "extraImage"].forEach((mediaName) => {
        const url = `${mediaName}Url`;
        if (uploadedUrls[url]) {
          questionData.append(url, uploadedUrls[url]);
        } else if (Object.prototype.hasOwnProperty.call(currentFormData, url)) {
          questionData.append(url, currentFormData[url] ?? "");
        }
      });

      let result;
      if (editingQuestion) {
        // For updates, we need to include the question ID and part ID in the URL
        result = await updateQuestion({
          examId,
          partId: part?._id,
          questionId: editingQuestion._id,
          questionData,
        });
      } else {
        // For new questions, include partId in the URL and send questionData as the body
        result = await addQuestion({
          examId,
          partId: part?._id,
          questionData,
        });
      }

      // Check if there was an error in the response
      if (result?.error) {
        throw new Error(
          result.error.data?.message || "Failed to save question"
        );
      }

      toast.success("Question saved successfully");
      delay(200)

      // Only close dialog and reset form if there were no errors
      setDialogOpen(false);
      setFormData({
        ...formData,
        question: "",
        options: ["", "", "", ""],
        correctAnswer: "",
        explanation: "",
        image: null,
        audio: null,
        extraImage: null,
        question_number: formData.question_number + 1,
      });
      // setIsSubmitting(false);
    } catch (error) {
      console.error("Error saving question:", error);
      // Show error message to the user and keep the dialog open
      setError(
        error.message ||
          "An error occurred while saving the question. Please try again."
      );
    }
    setIsSubmitting(false);
  };

  const handleDelete = async (question) => {
    if (window.confirm("Are you sure you want to delete this question?")) {
      try {
        await deleteQuestion({
          examId,
          partId: part?._id,
          questionId: question._id,
        }).unwrap();
      } catch (error) {
        console.error("Error deleting question:", error);
      }
    }
  };


  return (
    <Box>
      {examLoading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "400px",
            background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
            borderRadius: 3,
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            p: 4,
          }}
        >
          <CircularProgress
            size={60}
            sx={{
              color: "#2196F3",
              mb: 2,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              background:
                "linear-gradient(135deg, #2196F3 0%,rgb(12, 0, 245) 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontWeight: 600,
            }}
          >
            Loading Examinations...
          </Typography>
        </Box>
      ) : (
        <Box>
          <Accordion key={part?._id} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                Part {part?.partNumber} - {part?.title}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                sx={{ mb: 2, p: 2, border: "1px dashed grey", borderRadius: 1 }}
              >
                {part?.imageUrl && (
                  <Box mb={2} textAlign="center">
                    <img
                      src={part?.imageUrl}
                      alt={`Part ${part?.partNumber} illustration`}
                      style={{
                        maxWidth: "300px",
                        maxHeight: "300px",
                        borderRadius: "4px",
                      }}
                    />
                  </Box>
                )}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  <strong>Directions:</strong> {part?.instructions}
                </Typography>
              </Box>
              {/* )} */}
            </AccordionDetails>
          </Accordion>

          <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
            <Button
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog(null, part)}
            >
              Add Question to this Part
            </Button>
            {isRcPart && (
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => setPdfUploadOpen(true)}
              >
                Upload PDF (RC Part {part?.partNumber})
              </Button>
            )}
          </Box>
          {/* {questionsLoading ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : ( */}
          <PartQuestionList
            examId={examId}
            part={part}
            onEditQuestion={handleOpenDialog}
            onDeleteQuestion={handleDelete}
            setQuestions={setQuestions}
          />
        </Box>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle component="div">
          <Typography variant="h6">
            {editingQuestion ? "Edit Question" : "Add New Question"}
          </Typography>
        </DialogTitle>
        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{ m: 2, mb: 0 }}
          >
            {error}
          </Alert>
        )}
        {isSubmitting && (
          <Box sx={{ width: '100%' }}>
            <LinearProgress
              variant={uploadProgress > 0 ? "determinate" : "indeterminate"}
              value={uploadProgress}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
              {questionFormRef.current?.hasImages() 
                ? 'Uploading images and saving question...' 
                : 'Saving question...'}
            </Typography>
          </Box>
        )}
        <DialogContent>
          <QuestionForm
            ref={questionFormRef}
            initialData={formData}
            onChange={handleFormDataChange}
            previousQuestion={questions.length > 0 ? previousQuestion : null}
            showContent={part.section === SECTION.READING}
            isEditing={!!editingQuestion}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={isSubmitting} onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData?.correctAnswer || !part._id || isSubmitting}
          >
            {isSubmitting && <CircularProgress size={16} sx={{ mr: 1 }} />}
            {editingQuestion ? "Update" : "Add"} Question
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Upload Dialog (RC parts 5/6/7 only) */}
      {isRcPart && (
        <>
          <PdfUploadDialog
            open={pdfUploadOpen}
            onClose={() => setPdfUploadOpen(false)}
            title={`Parse RC PDF — Part ${part?.partNumber}`}
            description={`Upload a PDF containing TOEIC Reading questions. Only PART ${part?.partNumber} will be parsed. Nothing is saved yet — you will review and confirm before questions are added to "${examTitle || examData?.title || "this exam"}".`}
            uploadMutation={parsePdfMutation}
            defaultFormat="toeic-rc"
            lockFormat
            defaultOcrLang="eng"
            extraFields={[
              {
                name: "part",
                label: "PART to parse",
                type: "hidden",
                defaultValue: String(part?.partNumber || ""),
              },
            ]}
            onParsed={(payload) => {
              setPdfParseResult(payload);
              setPdfResultsOpen(true);
            }}
          />

          <PdfReviewDialog
            open={pdfResultsOpen}
            onClose={() => setPdfResultsOpen(false)}
            parsed={pdfParseResult}
            targetLabel={`Exam: ${examTitle || examData?.title || examId} (Part ${part?.partNumber})`}
            sectionDefault="reading"
            defaultPartNumber={part?.partNumber}
            uploadFolder={`toeic/exams/${examId}`}
            showContent
            submitMutation={bulkAddExamQuestionsTuple}
            buildBulkPayload={({ section, questions: payloadQuestions }) => ({
              examId,
              section,
              questions: payloadQuestions.map((q) => ({
                ...q,
                partNumber: q.partNumber ?? part?.partNumber,
              })),
            })}
          />
        </>
      )}
    </Box>
  );
};

export default ExamQuestionManagement;
